import { useMemo } from "react";
import messages from "messages";
// utils
import getFormScore from "../utils/getFormScore";
import isGAD7Form from "../utils/isGAD7Form";
import isPHQ9Form from "../utils/isPHQ9Form";
import getClasses from "utils/getClasses";
import getPHQ9ScoreRank from "../utils/getPHQ9ScoreRank";
import getGAD7ScoreRank from "../utils/getGAD7ScoreRank";
// styles
import styles from "./styles.module.scss";

type Props = {
  metaData: Partial<FormMetaData> | undefined;
  data: Record<string, any> | undefined;
};

/**
 * renders scoring on form (e.g. GAD-7/PHQ-9)
 */
const Scoring = ({ metaData, data }: Props) => {
  const { show, score, type, noticeable } = useMemo(() => {
    const isGAD7 = isGAD7Form(metaData?.form_json_schema?.title);
    const isPHQ9 = isPHQ9Form(metaData?.form_json_schema?.title);
    const res: {
      show: Boolean;
      score?: number;
    } & Partial<ReturnType<typeof getGAD7ScoreRank | typeof getPHQ9ScoreRank>> =
      {
        show: true,
      };
    res.show = !!(metaData?.completed_timestamp && (isGAD7 || isPHQ9));

    if (!res.show) {
      return res;
    }

    const score = getFormScore(data, metaData?.form_json_schema?.title);

    if (score === undefined) {
      return res;
    }
    if (isGAD7) {
      return { ...res, score, ...getGAD7ScoreRank(score) };
    }

    return { ...res, score, ...getPHQ9ScoreRank(score) };
  }, [data, metaData?.completed_timestamp, metaData?.form_json_schema?.title]);

  return !show || score === undefined ? null : (
    <p className={styles["scoring"]}>
      {`${metaData?.form_json_schema?.title} ${messages.totalScoreObtainedBy}`}
      <span className={styles["scoring__points"]}>{`${score},`}</span>
      <span
        className={getClasses(
          styles["scoring__tag"],
          noticeable && styles["scoring__tag--negative"],
        )}
      >
        {type}
      </span>
    </p>
  );
};

export default Scoring;
